import { type PaginationConfigs } from './pagination.types'

export const CONFIGS_BY_ID: PaginationConfigs = {
  'expense-summary': {
    defaultPagination: {
      orderBy: '-id',
    },
    mode: 'url',
  },
  'user-list': {
    defaultPagination: {
      orderBy: 'firstName',
    },
    mode: 'url',
  },
  role: {
    defaultPagination: {
      orderBy: '-name',
    },
    mode: 'local',
  },
  'audit-log': {
    mode: 'local',
  },
  'primary-category-list': {
    defaultPagination: {
      orderBy: 'name',
    },
    mode: 'url',
  },
  'reconciliation-transaction-list': {
    defaultPagination: {
      orderBy: '-transaction_datetime',
      pageSize: 50,
    },
    mode: 'url',
  },
  'transaction-list': {
    defaultPagination: {
      orderBy: '-transaction_datetime',
    },
    mode: 'url',
  },
  'detailed-card-list': {
    defaultPagination: {
      orderBy: 'cardholder_first_name',
    },
    mode: 'url',
  },
  'punchout-connection-logs': {
    defaultPagination: {
      orderBy: '-created_at',
    },
    mode: 'url',
  },
  'vendor-list': {
    defaultPagination: {
      orderBy: 'name',
    },
    mode: 'local',
  },
  'approvable-payment-list': {
    defaultPagination: {
      orderBy: '-submitted_at',
      pageSize: 100,
    },
    mode: 'url',
  },
  'approvable-expense-list': {
    defaultPagination: {
      orderBy: '-id',
    },
    mode: 'url',
  },
  // Order approvals list — `orderBy` here is the PUR_3723-off value; the list
  // presenter resolves the flag-derived key (`-order_number` when the flag is
  // on) and seeds it into the URL alongside page/page_size on first load.
  'order-approval-list': {
    defaultPagination: {
      orderBy: '-num',
      pageSize: 10,
    },
    mode: 'url',
  },
  'bill-approval-list': {
    defaultPagination: {
      orderBy: '-submitted_date',
    },
    mode: 'url',
  },
  'legal-entity-list': {
    defaultPagination: {
      orderBy: 'name',
    },
    mode: 'local',
  },
  'mileage-rate-preset-list': {
    mode: 'local',
  },
  'line-item-custom-field-list': {
    mode: 'url',
  },
  'header-custom-field-list': {
    mode: 'url',
  },
  'unbilled-expense-items': {
    defaultPagination: {
      orderBy: 'id',
    },
    mode: 'local',
  },
  'approved-order-items': {
    defaultPagination: {
      orderBy: '-approved_datetime',
    },
    mode: 'url',
  },
  'approved-expense-items': {
    defaultPagination: {
      orderBy: '-approved_datetime',
    },
    mode: 'url',
  },
  'ap-reconcile-accounts': {
    defaultPagination: {
      orderBy: 'name',
    },
    mode: 'url',
  },
  // Account-details item tabs — the items endpoint takes no order_by/search;
  // only page state syncs to the URL so tab links preserve it.
  'ap-reconcile-items': {
    mode: 'url',
  },
  // Matching-screen panes — pane filters are per-pane transient state, not
  // URL (BHV-052).
  'ap-reconcile-unmatched-items': {
    defaultPagination: {
      orderBy: '-item__received_on',
    },
    mode: 'local',
  },
  'ap-reconcile-unmatched-statement-items': {
    defaultPagination: {
      orderBy: 'date',
    },
    mode: 'local',
  },
  'ap-reconcile-matched': {
    mode: 'local',
  },
  // Overall Spend report tabs — no defaultPagination.orderBy on either id
  // (Angular applies no initial sort; server default order applies).
  'ap-overall-spend-secondary-category': {
    mode: 'url',
  },
  'ap-overall-spend-account-code': {
    mode: 'url',
  },
  RECEIVING_SUMMARY: {
    defaultPagination: {
      orderBy: '-procurify_PO',
      pageSize: 10,
    },
    mode: 'url',
  },
  // Spend report aggregate tables — default sort `-spent` (BHV-019).
  REPORT_VENDOR_SPEND: {
    defaultPagination: {
      orderBy: '-spent',
    },
    mode: 'url',
  },
  REPORT_USER_SPEND: {
    defaultPagination: {
      orderBy: '-spent',
    },
    mode: 'url',
  },
  REPORT_SECONDARY_CATEGORY_SPEND: {
    defaultPagination: {
      orderBy: '-spent',
    },
    mode: 'url',
  },
  REPORT_EXPENSE_SPEND: {
    defaultPagination: {
      orderBy: '-spent',
    },
    mode: 'url',
  },
  // Report detail line-item tables (BHV-026/028). REPORT_ORDER_LINE_ITEMS
  // is shared by three report configs with DIFFERENT default sorts
  // (`-account__account_code__code` vendor/department, `-total_cost` user)
  // — no config-level orderBy default here; the feature seeds it from
  // `reportConfig.detailDefaultSort` when the URL has no order param.
  REPORT_ORDER_LINE_ITEMS: {
    mode: 'url',
  },
  REPORT_EXPENSE_LINE_ITEMS: {
    defaultPagination: {
      orderBy: '-account__account_code__code',
    },
    mode: 'url',
  },
  // Report export history table — default sort -created_at (BHV-035).
  REPORT_EXPORT_HISTORY: {
    defaultPagination: {
      orderBy: '-created_at',
    },
    mode: 'url',
  },
  // Manage payment accounts tabs — sibling tab routes share one URL, so both
  // ids register with local mode (two url-mode ids would collide on the same
  // query-string params and carry stale page/search across tab switches).
  'bank-account-configs': {
    defaultPagination: {
      orderBy: 'name',
    },
    mode: 'local',
  },
  'credit-card-accounts': {
    defaultPagination: {
      orderBy: 'name',
    },
    mode: 'local',
  },
  // Requested-CSVs history table — default sort -created_at, page size 10
  // (BHV-030/035); url mode syncs filter/sort/page to the query string.
  'requested-exports': {
    defaultPagination: {
      orderBy: '-created_at',
      pageSize: 10,
    },
    mode: 'url',
  },
  // Travel requests status list — url mode so tab links preserve search and
  // reset page=1 via getQueryString({ page: 1 }).
  'travel-request-status': {
    defaultPagination: {
      orderBy: '-id',
    },
    mode: 'url',
  },
  // Travel approvals list — url mode so tab links preserve search and reset
  // page=1 via getQueryString({ page: 1 }).
  'travel-approval-list': {
    defaultPagination: {
      orderBy: '-id',
    },
    mode: 'url',
  },
  // QuickBooks Desktop account import lists — the endpoint takes no
  // order_by/search, and the Unimported/Imported tabs are sibling routes;
  // local mode keeps page state out of the query string.
  'quickbooks-unimported-accounts': {
    mode: 'local',
  },
  'quickbooks-imported-accounts': {
    mode: 'local',
  },
  // QuickBooks Desktop vendor import lists — same rationale as the account
  // lists above.
  'quickbooks-unimported-vendors': {
    mode: 'local',
  },
  'quickbooks-imported-vendors': {
    mode: 'local',
  },
  // QuickBooks Desktop export lists (one id per resource; the presenter
  // resets to page 1 when the Exportable/Exported/Archived sub-status tab
  // changes) — the endpoints take no order_by/search; local mode keeps page
  // state out of the query string like the import lists above.
  'quickbooks-po-exports': {
    mode: 'local',
  },
  'quickbooks-item-receipt-exports': {
    mode: 'local',
  },
  'quickbooks-bill-exports': {
    mode: 'local',
  },
  // QuickBooks Desktop Activity/Error Log table (one id shared by both log
  // types; the presenter resets to page 1 when the log tab changes) — the
  // endpoints take no order_by/search; local mode like the lists above.
  'quickbooks-logs': {
    mode: 'local',
  },
  // Dropdown options are paginated client-side on the field detail page, so
  // keep the page/limit out of the URL.
  'custom-field-dropdown-options': {
    mode: 'local',
  },
}
